/**
 * Field Configuration System
 * Centralized configuration for all admin form fields
 * Migrated from Alpine.js field-config.js
 *
 * This file merges auto-generated configs from backend schema with manual overrides.
 * Generated config: ./generated/fieldConfig.generated.ts
 * Regenerate with: pnpm run generate:config
 */

// Import types from separate file to avoid circular dependencies
// Re-export for backward compatibility
export {
  FieldType,
  ValidationRuleType,
  type ValidationRule,
  type ValidationResult,
  type SelectOption,
  type FieldSchema,
  type FieldConfig,
} from "./fieldConfig.types";

import {
  FieldType,
  ValidationRuleType,
  type FieldConfig,
  type ValidationResult,
} from "./fieldConfig.types";
import { GENERATED_FIELD_CONFIG } from "./generated/fieldConfig.generated";

/**
 * Helper function to get help text from schema
 * Handles string, function, or Record<string, string> (for select fields)
 */
export function getHelpText(
  help: string | (() => string) | Record<string, string> | undefined,
  currentValue?: string,
): string | undefined {
  if (!help) return undefined;
  if (typeof help === "function") return help();
  if (typeof help === "string") return help;
  // For Record<string, string>, return help for current value or first value
  if (typeof help === "object") {
    if (currentValue && help[currentValue]) {
      return help[currentValue];
    }
    // Return first help text as fallback
    const firstKey = Object.keys(help)[0];
    return firstKey ? help[firstKey] : undefined;
  }
  return undefined;
}

/**
 * Helper function to get nested config value using dot notation
 */
export function getConfigValue(obj: any, path: string): any {
  if (!path || typeof path !== "string") return undefined;
  if (!path.includes(".")) return obj?.[path];
  return path.split(".").reduce((current, key) => current?.[key], obj);
}

/**
 * Helper function to validate custom_tools configuration
 */
export function validateToolsConfig(parsed: any): ValidationResult {
  if (typeof parsed === "string") {
    return { valid: true };
  }

  if (Array.isArray(parsed)) {
    const allStrings = parsed.every((item) => typeof item === "string");
    if (!allStrings) {
      return {
        valid: false,
        isWarning: true,
        message: "Array must contain only module name strings",
      };
    }
    return { valid: true };
  }

  if (typeof parsed === "object" && parsed !== null) {
    if (!parsed.modules || !Array.isArray(parsed.modules)) {
      return {
        valid: false,
        isWarning: true,
        message: 'Object format must have "modules" array field',
      };
    }
    return { valid: true };
  }

  return {
    valid: false,
    isWarning: true,
    message: "Must be a string, array, or object",
  };
}

/**
 * Helper function to validate mcp_servers configuration
 */
export function validateMcpServersConfig(parsed: any): ValidationResult {
  if (typeof parsed === "string") {
    return { valid: true };
  }

  if (Array.isArray(parsed)) {
    const allStrings = parsed.every((item) => typeof item === "string");
    if (!allStrings) {
      return {
        valid: false,
        isWarning: true,
        message: "Array must contain only server ID strings",
      };
    }
    return { valid: true };
  }

  if (typeof parsed === "object" && parsed !== null) {
    if (!parsed.servers || !Array.isArray(parsed.servers)) {
      return {
        valid: false,
        isWarning: true,
        message: 'Object format must have "servers" array field',
      };
    }
    return { valid: true };
  }

  return {
    valid: false,
    isWarning: true,
    message: "Must be a string, array, or object",
  };
}

/**
 * Helper function to validate team_members configuration
 */
export function validateTeamMembersConfig(parsed: any): ValidationResult {
  if (typeof parsed === "string") {
    return { valid: true };
  }

  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      if (typeof item === "string") continue;
      if (typeof item === "object" && item !== null) {
        if (!item.agent_type && !item.type) {
          return {
            valid: false,
            isWarning: true,
            message: 'Member object must have "agent_type" field',
          };
        }
        continue;
      }
      return {
        valid: false,
        isWarning: true,
        message: "Array items must be strings or member config objects",
      };
    }
    return { valid: true };
  }

  if (typeof parsed === "object" && parsed !== null) {
    if (!parsed.members || !Array.isArray(parsed.members)) {
      return {
        valid: false,
        isWarning: true,
        message: 'Object format must have "members" array field',
      };
    }
    return validateTeamMembersConfig(parsed.members);
  }

  return {
    valid: false,
    isWarning: true,
    message: "Must be a string, array, or object with members",
  };
}

/**
 * Main field configuration
 * All field definitions for the agent configuration form
 */
export const FIELD_CONFIG: FieldConfig = {
  // ========== Basic Information ==========
  agent_type: {
    type: FieldType.TEXT,
    label: "Agent Type",
    help: "Unique identifier for this agent type",
    section: "basic",
    readonly: true,
    required: true,
    default: "",
  },

  name: {
    type: FieldType.TEXT,
    label: "Name",
    placeholder: "Enter agent name",
    help: "Display name for the agent",
    section: "basic",
    required: true,
    default: "",
    validation: [
      { rule: ValidationRuleType.REQUIRED },
      { rule: ValidationRuleType.MIN_LENGTH, value: 1 },
      { rule: ValidationRuleType.MAX_LENGTH, value: 100 },
    ],
  },

  description: {
    type: FieldType.TEXTAREA,
    label: "Description",
    placeholder: "Enter agent description",
    help: "Brief description of the agent's purpose and capabilities",
    section: "basic",
    rows: 3,
    default: "",
    validation: [{ rule: ValidationRuleType.MAX_LENGTH, value: 500 }],
  },

  // NOTE: Legacy model fields (multimodal_model_id, chat_model_id, embedding_model_id) removed
  // Use unified models.* configuration instead:
  // - models.chat.model_id, models.chat.quick_model_id
  // - models.vision.model_id, models.vision.quick_model_id
  // - models.embedding.model_id

  // ========== Chat Model Configuration ==========
  "models.chat.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "主对话模型",
    help: "用于深度响应的主要对话模型（复杂分析、咨询等）",
    section: "models",
    required: true,
    optionsSource: "chatModels",
    default: "claude-haiku-4.5",
    isPrimary: true,
    linkedParameters: [
      "models.chat.temperature",
      "models.chat.top_p",
      "models.chat.max_tokens",
    ],
  },

  "models.chat.quick_model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "快速对话模型",
    help: "用于快速响应的对话模型（问候、确认等）。留空则使用主对话模型。",
    section: "models",
    optionsSource: "chatModels",
    allowEmpty: true,
    emptyLabel: "使用主对话模型",
    default: "deepseek-v3.2",
  },

  // NOTE: These parameter fields are integrated into the primary model selector
  // They are hidden from standalone display but included via linkedParameters
  "models.chat.temperature": {
    type: FieldType.NUMBER,
    label: "温度",
    help: "采样温度（0=确定性, 2=创造性）",
    section: "models",
    min: 0,
    max: 2,
    step: 0.1,
    default: 0.7,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.chat.top_p": {
    type: FieldType.NUMBER,
    label: "Top P",
    help: "核采样参数",
    section: "models",
    min: 0,
    max: 1,
    step: 0.1,
    default: 1.0,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.chat.max_tokens": {
    type: FieldType.NUMBER,
    label: "最大 Token",
    help: "最大 Token 数（留空=无限制）",
    section: "models",
    min: 1,
    max: 128000,
    default: null,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  // ========== Vision Model Configuration ==========
  "models.vision.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "视觉模型",
    help: "主对话模型的视觉补充。当主对话模型不支持图片理解时，使用此模型处理图片请求。如主模型已支持视觉（如 GPT-4o），则无需配置。",
    section: "models",
    optionsSource: "visionModels",
    allowEmpty: true,
    emptyLabel: "禁用",
    default: null,
    linkedParameters: [
      "models.vision.temperature",
      "models.vision.top_p",
      "models.vision.max_tokens",
    ],
  },

  "models.vision.quick_model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "快速视觉模型",
    help: "快速对话模型的视觉补充。当快速对话模型不支持图片理解时，使用此模型。留空则回退到主视觉模型。",
    section: "models",
    optionsSource: "visionModels",
    allowEmpty: true,
    emptyLabel: "使用主模型",
    default: null,
  },

  "models.vision.temperature": {
    type: FieldType.NUMBER,
    label: "温度",
    help: "视觉模型采样温度（默认: 0.7）",
    section: "models",
    min: 0,
    max: 2,
    step: 0.1,
    default: 0.7,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.vision.top_p": {
    type: FieldType.NUMBER,
    label: "Top P",
    help: "核采样阈值（默认: 1.0）",
    section: "models",
    min: 0,
    max: 1,
    step: 0.1,
    default: 1.0,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.vision.max_tokens": {
    type: FieldType.NUMBER,
    label: "最大 Token 数",
    help: "视觉理解的最大输出 token 数",
    section: "models",
    min: 1,
    max: 128000,
    default: null,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  // ========== Memory Model Configuration ==========
  "models.memory.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "记忆提取模型",
    help: "用于记忆提取和摘要的模型（推荐使用性价比高的模型）",
    section: "models",
    optionsSource: "memoryModels",
    default: "gpt-5-mini",
    linkedParameters: [
      "models.memory.temperature",
      "models.memory.top_p",
      "models.memory.max_tokens",
    ],
  },

  "models.memory.temperature": {
    type: FieldType.NUMBER,
    label: "温度",
    help: "较低的温度有利于准确的记忆提取（默认: 0.5）",
    section: "models",
    min: 0,
    max: 2,
    step: 0.1,
    default: 0.5,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.memory.top_p": {
    type: FieldType.NUMBER,
    label: "Top P",
    help: "核采样阈值（默认: 1.0）",
    section: "models",
    min: 0,
    max: 1,
    step: 0.1,
    default: 1.0,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.memory.max_tokens": {
    type: FieldType.NUMBER,
    label: "最大 Token 数",
    help: "记忆提取的最大 token 数（默认: 2000）",
    section: "models",
    min: 1,
    max: 128000,
    default: 2000,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  // ========== Classification Model Configuration ==========
  "models.classification.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "意图分类模型",
    help: "用于意图分类的模型",
    section: "models",
    optionsSource: "memoryModels",
    default: "gpt-5-mini",
    linkedParameters: [
      "models.classification.temperature",
      "models.classification.top_p",
      "models.classification.max_tokens",
    ],
  },

  "models.classification.temperature": {
    type: FieldType.NUMBER,
    label: "温度",
    help: "较低的温度有利于一致的分类结果（默认: 0.3）",
    section: "models",
    min: 0,
    max: 2,
    step: 0.1,
    default: 0.3,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.classification.top_p": {
    type: FieldType.NUMBER,
    label: "Top P",
    help: "核采样阈值（默认: 1.0）",
    section: "models",
    min: 0,
    max: 1,
    step: 0.1,
    default: 1.0,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.classification.max_tokens": {
    type: FieldType.NUMBER,
    label: "最大 Token 数",
    help: "分类输出的最大 token 数（默认: 500）",
    section: "models",
    min: 1,
    max: 128000,
    default: 500,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  // NOTE: models.reasoning.* fields removed - use reasoning.* fields instead

  // ========== Task Model Configuration ==========
  "models.task.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "任务模型",
    help: "用于子任务的轻量模型（压缩、分类、摘要等）。留空则使用主对话模型。",
    section: "models",
    optionsSource: "memoryModels",
    allowEmpty: true,
    emptyLabel: "使用主对话模型",
    default: null,
    linkedParameters: ["models.task.temperature", "models.task.max_tokens"],
  },

  "models.task.temperature": {
    type: FieldType.NUMBER,
    label: "温度",
    help: "较低的温度使任务输出更确定性（默认: 0.3）",
    section: "models",
    min: 0,
    max: 2,
    step: 0.1,
    default: 0.3,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.task.max_tokens": {
    type: FieldType.NUMBER,
    label: "最大 Token 数",
    help: "任务输出的最大 token 数（默认: 2000）",
    section: "models",
    min: 1,
    max: 32000,
    default: 2000,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  // ========== Embedding & Retrieval Configuration ==========
  "models.embedding.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "向量模型",
    help: "用于向量嵌入的模型（知识库、记忆搜索）",
    section: "models",
    optionsSource: "embeddingModels",
    default: "BAAI/bge-m3",
  },

  "models.reranking.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "重排序模型",
    help: "用于搜索结果重排序的模型。留空则禁用重排序。",
    section: "models",
    optionsSource: "rerankingModels",
    allowEmpty: true,
    emptyLabel: "禁用",
    default: "BAAI/bge-reranker-v2-m3",
    linkedParameters: ["models.reranking.top_k"],
  },

  "models.reranking.top_k": {
    type: FieldType.NUMBER,
    label: "返回数量",
    help: "重排序后返回的结果数量",
    section: "models",
    min: 1,
    max: 100,
    default: 10,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  // ========== Speech Model Configuration ==========
  "models.stt.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "语音模型",
    help: "用于语音输入的语音转文字模型。留空则禁用语音功能。",
    section: "models",
    optionsSource: "sttModels",
    allowEmpty: true,
    emptyLabel: "禁用",
    default: null,
    linkedParameters: ["models.stt.language"],
  },

  "models.stt.language": {
    type: FieldType.SELECT,
    label: "识别语言",
    help: "语言提示以提高识别准确率",
    section: "models",
    options: [
      { value: "", label: "自动" },
      { value: "zh", label: "中文" },
      { value: "en", label: "英语" },
      { value: "ja", label: "日语" },
      { value: "ko", label: "韩语" },
    ],
    default: "",
    buttonGroup: true,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  // ========== Reasoning/Thinking Configuration ==========
  // Reasoning modes:
  // - empty model → disabled or native mode (use main model's thinking capability)
  // - different model → combined mode (separate reasoning model)
  "models.reasoning.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "推理模型",
    help: "留空则使用主对话模型的思考能力（原生模式）；选择其他模型则使用组合模式。",
    section: "models",
    optionsSource: "reasoningModels",
    allowEmpty: true,
    emptyLabel: "使用主对话模型",
    default: null,
    linkedParameters: [
      "models.reasoning.budget_tokens",
      "models.reasoning.temperature",
      "models.reasoning.top_p",
      "models.reasoning.max_tokens",
    ],
  },

  // NOTE: reasoning.mode removed from UI - auto-determined by model selection:
  // - empty model_id → native mode
  // - different model_id → combined mode

  "models.reasoning.budget_tokens": {
    type: FieldType.SELECT,
    label: "思考深度",
    help: {
      "": "自动：让模型自行决定。",
      "1024": "快速：~1K tokens。",
      "4096": "平衡：~4K tokens。",
      "8192": "深度：~8K tokens。",
      "16384": "极限：~16K tokens。",
    },
    section: "models",
    options: [
      { value: "", label: "自动" },
      { value: "1024", label: "1K" },
      { value: "4096", label: "4K" },
      { value: "8192", label: "8K" },
      { value: "16384", label: "16K" },
    ],
    default: "",
    buttonGroup: true,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.reasoning.temperature": {
    type: FieldType.NUMBER,
    label: "温度",
    help: "推理模型采样温度（默认: 0.7）",
    section: "models",
    min: 0,
    max: 2,
    step: 0.1,
    default: 0.7,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.reasoning.top_p": {
    type: FieldType.NUMBER,
    label: "Top P",
    help: "核采样阈值（默认: 1.0）",
    section: "models",
    min: 0,
    max: 1,
    step: 0.1,
    default: 1.0,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  "models.reasoning.max_tokens": {
    type: FieldType.NUMBER,
    label: "最大 Token 数",
    help: "推理输出的最大 token 数",
    section: "models",
    min: 1,
    max: 128000,
    default: null,
    showIf: () => false, // Hidden - shown via linkedParameters
  },

  // NOTE: llm_parameters section removed - use models.chat.* fields instead

  // ========== Output Format ==========
  output_format: {
    type: FieldType.OUTPUT_FORMAT,
    label: "Output Format",
    help: "Response output format: default (plain text), markdown, or JSON",
    section: "basic",
    default: "default",
    options: [
      { value: "default", label: "Default" },
      { value: "markdown", label: "Markdown" },
      { value: "json", label: "JSON" },
    ],
  },

  // Note: default_output_schema_key removed - use is_default on OutputSchemaConfig instead

  // ========== Features ==========
  debug_mode: {
    type: FieldType.CHECKBOX,
    label: "Debug Mode",
    help: "Show detailed execution information in responses",
    section: "features",
    default: false,
  },

  storage_enabled: {
    type: FieldType.CHECKBOX,
    label: "Enable Session Storage",
    help: "Store chat sessions in database (required for session history)",
    section: "features",
    default: true,
  },

  knowledge_enabled: {
    type: FieldType.CHECKBOX,
    label: "Enable Knowledge Base",
    help: "Use vector knowledge base for context-aware responses",
    section: "features",
    default: false,
  },

  "hooks.astrology_plus.enabled": {
    type: FieldType.CHECKBOX,
    label: "Enable Birth Chart Context",
    help: "Include user birth chart data in prompts (astrology agents)",
    section: "features",
    default: false,
  },

  "hooks.astrology_plus.include_natal_chart": {
    type: FieldType.CHECKBOX,
    label: "Include Natal Chart Calculation",
    help: "Include full natal chart in compact LLM-friendly format (~300-400 bytes). Uses astrological symbols: ☉♎26°H6 ☽♎0°H5. Calculates planets, aspects, and patterns using astro-tool.",
    section: "features",
    default: false,
    showIf: (config) =>
      getConfigValue(config, "hooks.astrology_plus.enabled") === true,
  },

  "context.datetime_enabled": {
    type: FieldType.CHECKBOX,
    label: "Add Current DateTime",
    help: "Include current date/time in system instructions",
    section: "features",
    default: false,
  },

  // ========== Message Processing ==========
  add_history_to_context: {
    type: FieldType.CHECKBOX,
    label: "Add History to Context",
    help: "Include previous session runs in context",
    section: "message_processing",
    default: false,
  },

  num_history_runs: {
    type: FieldType.NUMBER,
    label: "Number of History Runs",
    help: "How many previous runs to include",
    section: "message_processing",
    min: 1,
    max: 20,
    default: 3,
    showIf: (config) => config.add_history_to_context === true,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 20 },
    ],
  },

  num_history_messages: {
    type: FieldType.NUMBER,
    label: "Max History Messages",
    help: "Max total messages across all history runs. Leave empty for unlimited. Useful as a hard cap to prevent context overflow.",
    section: "message_processing",
    min: 1,
    max: 200,
    default: null,
    showIf: (config) => config.add_history_to_context === true,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 200 },
    ],
  },

  max_tool_calls_from_history: {
    type: FieldType.NUMBER,
    label: "Max Tool Calls in History",
    help: "Max tool call results to keep in history. Tool results are often large and consume significant context. Leave empty for unlimited.",
    section: "message_processing",
    min: 0,
    max: 50,
    default: null,
    showIf: (config) => config.add_history_to_context === true,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0 },
      { rule: ValidationRuleType.MAX, value: 50 },
    ],
  },

  read_chat_history: {
    type: FieldType.CHECKBOX,
    label: "Read Chat History",
    help: "Include full chat history in context",
    section: "message_processing",
    default: false,
  },

  // ========== Session Summary Configuration ==========
  "session_summary.enabled": {
    type: FieldType.CHECKBOX,
    label: "Enable Session Summary",
    help: "Enable automatic session summary generation. Summaries provide compressed conversation history for long conversations.",
    section: "session_summary",
    default: false,
  },

  // NOTE: session_summary.model_id REMOVED - use models.memory.model_id instead
  // The memory model is now shared for all memory-related operations including session summary

  "session_summary.summary_frequency": {
    type: FieldType.SELECT,
    label: "Summary Frequency",
    help: {
      on_request: "Generate summary only when explicitly requested via API",
      after_n_messages:
        "Generate summary automatically after N messages (recommended)",
      end_of_session:
        "Generate summary at the end of each session (manual trigger)",
    },
    section: "session_summary",
    options: [
      { value: "on_request", label: "On Request" },
      { value: "after_n_messages", label: "After N Messages" },
      { value: "end_of_session", label: "End of Session" },
    ],
    default: "on_request",
    showIf: (config) =>
      getConfigValue(config, "session_summary.enabled") === true,
    validation: [
      {
        rule: ValidationRuleType.IN_OPTIONS,
        options: ["on_request", "after_n_messages", "end_of_session"],
      },
    ],
  },

  "session_summary.message_threshold": {
    type: FieldType.NUMBER,
    label: "Message Threshold",
    help: "Number of messages before first summary generation. Recommended: Set equal to num_history_runs to avoid information gaps.",
    section: "session_summary",
    min: 1,
    max: 50,
    step: 1,
    default: 10,
    showIf: (config) =>
      getConfigValue(config, "session_summary.enabled") === true &&
      getConfigValue(config, "session_summary.summary_frequency") ===
        "after_n_messages",
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 50 },
    ],
  },

  "session_summary.regenerate_interval": {
    type: FieldType.NUMBER,
    label: "Regenerate Interval",
    help: "How often to regenerate summary after initial generation. 0 = never regenerate. Recommended: Set equal to num_history_runs for rolling coverage.",
    section: "session_summary",
    min: 0,
    max: 50,
    step: 1,
    default: 0,
    showIf: (config) =>
      getConfigValue(config, "session_summary.enabled") === true &&
      getConfigValue(config, "session_summary.summary_frequency") ===
        "after_n_messages",
    validation: [
      { rule: ValidationRuleType.MIN, value: 0 },
      { rule: ValidationRuleType.MAX, value: 50 },
    ],
  },

  "session_summary.inject_into_prompt": {
    type: FieldType.CHECKBOX,
    label: "Inject into Prompt",
    help: "Inject summary into system prompt as dependency data for context-aware responses.",
    section: "session_summary",
    default: true,
    showIf: (config) =>
      getConfigValue(config, "session_summary.enabled") === true,
  },

  "session_summary.custom_prompt": {
    type: FieldType.TEXTAREA,
    label: "Custom Summary Prompt",
    help: "Custom prompt template for summary generation. Leave empty to use default Agno prompt.",
    placeholder: "Enter custom prompt for summary generation...",
    section: "session_summary",
    rows: 6,
    default: null,
    showIf: (config) =>
      getConfigValue(config, "session_summary.enabled") === true,
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          if (value.length > 2000) {
            return {
              valid: false,
              message: "Custom prompt cannot exceed 2000 characters",
            };
          }
          return { valid: true };
        },
      },
    ],
  },

  // ========== Advanced Settings ==========
  session_timeout_minutes: {
    type: FieldType.NUMBER,
    label: "Session Timeout (minutes)",
    help: "Session expires after this many minutes of inactivity",
    section: "advanced",
    min: 1,
    max: 1440,
    default: 60,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 1440 },
    ],
  },

  max_cached_resources: {
    type: FieldType.NUMBER,
    label: "Max Cached Resources",
    help: "Maximum number of resources to cache per user",
    section: "advanced",
    min: 1,
    max: 100,
    default: 10,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 100 },
    ],
  },

  resource_ttl_minutes: {
    type: FieldType.NUMBER,
    label: "Resource TTL (minutes)",
    help: "How long to cache resources before expiring",
    section: "advanced",
    min: 1,
    max: 1440,
    default: 30,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 1440 },
    ],
  },

  // ========== Custom Tools ==========
  custom_tools: {
    type: FieldType.JSON,
    label: "Custom Tools Configuration",
    help: "Configure custom tool modules for this agent",
    section: "custom_tools",
    default: null,
    placeholder: '{\n  "modules": ["tools.astrology"]\n}',
    displayMode: "list",
    hasToolBrowser: true,
    readonly: true,
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value) return { valid: true };
          try {
            const parsed =
              typeof value === "string" ? JSON.parse(value) : value;
            return validateToolsConfig(parsed);
          } catch (e: any) {
            return { valid: false, message: `Invalid JSON: ${e.message}` };
          }
        },
      },
    ],
  },

  // ========== Builtin Tools ==========
  builtin_tools: {
    type: FieldType.JSON,
    label: "Builtin Tools Configuration",
    help: "Enable/disable builtin tools (web search, calculator, etc.)",
    section: "builtin_tools",
    default: null,
    placeholder: '{\n  "web_search": true\n}',
    displayMode: "list",
    hasToolBrowser: true,
    readonly: true,
  },

  // ========== MCP Servers ==========
  mcp_servers: {
    type: FieldType.JSON,
    label: "MCP Servers Configuration",
    help: "Configure Model Context Protocol servers for tool access",
    section: "mcp_tools",
    default: null,
    placeholder: '["astro-tool"]',
    displayMode: "list",
    hasToolBrowser: true,
    readonly: true,
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value) return { valid: true };
          try {
            const parsed =
              typeof value === "string" ? JSON.parse(value) : value;
            return validateMcpServersConfig(parsed);
          } catch (e: any) {
            return { valid: false, message: `Invalid JSON: ${e.message}` };
          }
        },
      },
    ],
  },

  // ========== Team Configuration ==========
  // Note: agent_mode is controlled by the team toggle in HooksPipeline
  // Values: 'single' (default) or 'team'
  // This field is hidden from Team tab - use pipeline toggle instead

  team_members: {
    type: FieldType.AGENT_MULTI_SELECT,
    label: "团队成员",
    help: "选择参与协作的团队成员Agent",
    section: "team",
    default: [],
    showIf: (config) => config.agent_mode === "team",
  },

  respond_directly: {
    type: FieldType.CHECKBOX,
    label: "Respond Directly",
    help: "Leader responds directly without delegating",
    section: "team",
    default: false,
    showIf: (config) => config.agent_mode === "team",
  },

  delegate_to_all_members: {
    type: FieldType.CHECKBOX,
    label: "Delegate to All Members",
    help: "Send task to all team members simultaneously",
    section: "team",
    default: false,
    showIf: (config) => config.agent_mode === "team",
  },

  share_member_interactions: {
    type: FieldType.CHECKBOX,
    label: "Share Member Interactions",
    help: "Share interactions between team members",
    section: "team",
    default: false,
    showIf: (config) => config.agent_mode === "team",
  },

  show_members_responses: {
    type: FieldType.CHECKBOX,
    label: "Show Member Responses",
    help: "Display individual member responses to user",
    section: "team",
    default: false,
    showIf: (config) => config.agent_mode === "team",
  },

  add_team_history_to_members: {
    type: FieldType.CHECKBOX,
    label: "Add Team History to Members",
    help: "Include team conversation history for each member",
    section: "team",
    default: false,
    showIf: (config) => config.agent_mode === "team",
  },

  determine_input_for_members: {
    type: FieldType.CHECKBOX,
    label: "Determine Input for Members",
    help: "Leader determines specific input for each member",
    section: "team",
    default: false,
    showIf: (config) => config.agent_mode === "team",
  },

  // ========== Memory Configuration ==========
  "memory.enabled": {
    type: FieldType.CHECKBOX,
    label: "Enable Memory",
    help: "Enable long-term memory storage",
    section: "memory",
    default: true,
  },

  "memory.provider": {
    type: FieldType.SELECT,
    label: "Memory Provider",
    help: "Choose memory storage backend. MEM0 provides advanced features like custom prompts, categories, and decay. PostgreSQL provides basic memory storage.",
    section: "memory",
    options: [
      { value: "MEM0", label: "MEM0 (Advanced)" },
      { value: "POSTGRES", label: "PostgreSQL (Basic)" },
      { value: "NONE", label: "None" },
    ],
    default: "MEM0",
    showIf: (config) => getConfigValue(config, "memory.enabled") === true,
    validation: [
      {
        rule: ValidationRuleType.IN_OPTIONS,
        options: ["MEM0", "POSTGRES", "NONE"],
      },
    ],
    invalidMessage: (value) =>
      `Warning: Provider '${value}' is not valid. Expected: MEM0, POSTGRES, or NONE`,
  },

  // NOTE: memory.model_id REMOVED - use models.memory.model_id instead
  // Memory model is now configured in BasicTab's models_memory section

  "memory.enable_agentic_memory": {
    type: FieldType.CHECKBOX,
    label: "Agentic Memory",
    help: "Enable agentic memory management - adds memory control tools to the agent. For MEM0: adds search/add/update/delete memory tools. For PostgreSQL: adds memory search/save tools. When enabled, the agent can actively manage its own memories.",
    section: "memory",
    default: true,
    showIf: (config) => getConfigValue(config, "memory.enabled") === true,
  },

  // ========== Custom Prompts Toggle (MEM0 only) ==========
  "memory.prompts.enable_custom_prompts": {
    type: FieldType.CHECKBOX,
    label: "Enable Custom Prompts",
    help: "Enable Mem0 custom prompts feature for this agent (Dual Prompt Architecture: User + Agent)",
    section: "memory_prompts",
    default: false,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0",
  },

  // ========== User Memory Section (MEM0 only) ==========
  "memory.user_memory_enabled": {
    type: FieldType.CHECKBOX,
    label: "Enable User Memory",
    help: "Extract User Memory (facts, preferences from user messages). When disabled, only Agent Memory will be extracted.",
    section: "memory_user",
    default: true,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0",
  },

  "memory.prompts.user_memory_extraction_mode": {
    type: FieldType.SELECT,
    label: "User Memory Extraction Mode",
    help: "Choose to use system default or custom prompt for extracting memories from USER messages",
    section: "memory_user",
    options: [
      { value: "system", label: "Use System Default" },
      { value: "custom", label: "Use Custom Prompt" },
    ],
    default: "system",
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.user_memory_enabled") === true &&
      getConfigValue(config, "memory.prompts.enable_custom_prompts") === true,
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ["system", "custom"] },
    ],
  },

  "memory.prompts.user_memory_extraction_prompt": {
    type: FieldType.TEXTAREA,
    label: "Custom User Memory Extraction Prompt",
    help: 'Custom prompt for extracting memories from USER messages (preferences, facts, plans). Click "Reset to System Default" to load the default value.',
    placeholder:
      "Enter custom user memory extraction prompt (50-4000 characters)...",
    section: "memory_user",
    rows: 10,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.user_memory_enabled") === true &&
      getConfigValue(config, "memory.prompts.enable_custom_prompts") === true &&
      getConfigValue(config, "memory.prompts.user_memory_extraction_mode") ===
        "custom",
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          if (value.length < 50) {
            return {
              valid: false,
              message:
                "User memory extraction prompt must be at least 50 characters if provided",
            };
          }
          if (value.length > 4000) {
            return {
              valid: false,
              message:
                "User memory extraction prompt cannot exceed 4000 characters",
            };
          }
          return { valid: true };
        },
      },
    ],
  },

  "memory.categories.enable_user_custom_categories": {
    type: FieldType.CHECKBOX,
    label: "Enable User Custom Categories",
    help: "Enable custom memory categories for USER memories (preferences, facts, plans)",
    section: "memory_user",
    default: false,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.user_memory_enabled") === true,
  },

  "memory.categories.user_categories_mode": {
    type: FieldType.SELECT,
    label: "User Categories Mode",
    help: "Choose to use system default or custom categories for user memories",
    section: "memory_user",
    options: [
      { value: "system", label: "Use System Default" },
      { value: "custom", label: "Use Custom Categories" },
    ],
    default: "system",
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.user_memory_enabled") === true &&
      getConfigValue(
        config,
        "memory.categories.enable_user_custom_categories",
      ) === true,
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ["system", "custom"] },
    ],
  },

  "memory.categories.user_custom_categories": {
    type: FieldType.TEXTAREA,
    label: "User Custom Categories (JSON)",
    help: 'Define custom USER memory categories in JSON format. Each category must have: description (string), examples (array), temporal (boolean). Click "Reset to System Default" to load the default categories.',
    placeholder:
      '{\n  "preference": {\n    "description": "User preferences",\n    "examples": ["likes pizza", "dislikes sweets"],\n    "temporal": false\n  }\n}',
    section: "memory_user",
    rows: 15,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.user_memory_enabled") === true &&
      getConfigValue(
        config,
        "memory.categories.enable_user_custom_categories",
      ) === true &&
      getConfigValue(config, "memory.categories.user_categories_mode") ===
        "custom",
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          try {
            const categories = JSON.parse(value);
            if (
              typeof categories !== "object" ||
              categories === null ||
              Array.isArray(categories)
            ) {
              return {
                valid: false,
                message: "User custom categories must be a JSON object",
              };
            }
            for (const [catName, catConfig] of Object.entries(categories)) {
              const cat = catConfig as any;
              if (typeof cat !== "object" || cat === null) {
                return {
                  valid: false,
                  message: `Category '${catName}' must be an object`,
                };
              }
              if (!cat.description || typeof cat.description !== "string") {
                return {
                  valid: false,
                  message: `Category '${catName}' must have a 'description' string field`,
                };
              }
              if (!Array.isArray(cat.examples)) {
                return {
                  valid: false,
                  message: `Category '${catName}' must have an 'examples' array field`,
                };
              }
              if (typeof cat.temporal !== "boolean") {
                return {
                  valid: false,
                  message: `Category '${catName}' must have a 'temporal' boolean field`,
                };
              }
            }
            return { valid: true };
          } catch (e: any) {
            return {
              valid: false,
              message: `Invalid JSON format: ${e.message}`,
            };
          }
        },
      },
    ],
  },

  // ========== Agent Memory Section (MEM0 only) ==========
  "memory.agent_memory_enabled": {
    type: FieldType.CHECKBOX,
    label: "Enable Agent Memory",
    help: "Extract Agent Memory (patterns, recommendations from assistant responses). When disabled, only User Memory will be extracted.",
    section: "memory_agent",
    default: true,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0",
  },

  "memory.prompts.agent_memory_extraction_mode": {
    type: FieldType.SELECT,
    label: "Agent Memory Extraction Mode",
    help: "Choose to use system default or custom prompt for extracting memories from ASSISTANT messages",
    section: "memory_agent",
    options: [
      { value: "system", label: "Use System Default" },
      { value: "custom", label: "Use Custom Prompt" },
    ],
    default: "system",
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.agent_memory_enabled") === true &&
      getConfigValue(config, "memory.prompts.enable_custom_prompts") === true,
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ["system", "custom"] },
    ],
  },

  "memory.prompts.agent_memory_extraction_prompt": {
    type: FieldType.TEXTAREA,
    label: "Custom Agent Memory Extraction Prompt",
    help: 'Custom prompt for extracting memories from ASSISTANT messages (recommendations, warnings, context). Click "Reset to System Default" to load the default value.',
    placeholder:
      "Enter custom agent memory extraction prompt (50-4000 characters)...",
    section: "memory_agent",
    rows: 10,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.agent_memory_enabled") === true &&
      getConfigValue(config, "memory.prompts.enable_custom_prompts") === true &&
      getConfigValue(config, "memory.prompts.agent_memory_extraction_mode") ===
        "custom",
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          if (value.length < 50) {
            return {
              valid: false,
              message:
                "Agent memory extraction prompt must be at least 50 characters if provided",
            };
          }
          if (value.length > 4000) {
            return {
              valid: false,
              message:
                "Agent memory extraction prompt cannot exceed 4000 characters",
            };
          }
          return { valid: true };
        },
      },
    ],
  },

  "memory.categories.enable_agent_custom_categories": {
    type: FieldType.CHECKBOX,
    label: "Enable Agent Custom Categories",
    help: "Enable custom memory categories for AGENT memories (recommendations, warnings, context)",
    section: "memory_agent",
    default: false,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.agent_memory_enabled") === true,
  },

  "memory.categories.agent_categories_mode": {
    type: FieldType.SELECT,
    label: "Agent Categories Mode",
    help: "Choose to use system default or custom categories for agent memories",
    section: "memory_agent",
    options: [
      { value: "system", label: "Use System Default" },
      { value: "custom", label: "Use Custom Categories" },
    ],
    default: "system",
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.agent_memory_enabled") === true &&
      getConfigValue(
        config,
        "memory.categories.enable_agent_custom_categories",
      ) === true,
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ["system", "custom"] },
    ],
  },

  "memory.categories.agent_custom_categories": {
    type: FieldType.TEXTAREA,
    label: "Agent Custom Categories (JSON)",
    help: 'Define custom AGENT memory categories in JSON format. Each category must have: description (string), examples (array), temporal (boolean). Click "Reset to System Default" to load the default categories.',
    placeholder:
      '{\n  "recommendation": {\n    "description": "Recommendations",\n    "examples": ["exercise daily", "learn Python"],\n    "temporal": false\n  }\n}',
    section: "memory_agent",
    rows: 15,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.agent_memory_enabled") === true &&
      getConfigValue(
        config,
        "memory.categories.enable_agent_custom_categories",
      ) === true &&
      getConfigValue(config, "memory.categories.agent_categories_mode") ===
        "custom",
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          try {
            const categories = JSON.parse(value);
            if (
              typeof categories !== "object" ||
              categories === null ||
              Array.isArray(categories)
            ) {
              return {
                valid: false,
                message: "Agent custom categories must be a JSON object",
              };
            }
            for (const [catName, catConfig] of Object.entries(categories)) {
              const cat = catConfig as any;
              if (typeof cat !== "object" || cat === null) {
                return {
                  valid: false,
                  message: `Category '${catName}' must be an object`,
                };
              }
              if (!cat.description || typeof cat.description !== "string") {
                return {
                  valid: false,
                  message: `Category '${catName}' must have a 'description' string field`,
                };
              }
              if (!Array.isArray(cat.examples)) {
                return {
                  valid: false,
                  message: `Category '${catName}' must have an 'examples' array field`,
                };
              }
              if (typeof cat.temporal !== "boolean") {
                return {
                  valid: false,
                  message: `Category '${catName}' must have a 'temporal' boolean field`,
                };
              }
            }
            return { valid: true };
          } catch (e: any) {
            return {
              valid: false,
              message: `Invalid JSON format: ${e.message}`,
            };
          }
        },
      },
    ],
  },

  // ========== Shared Memory Update Prompt (MEM0 only) ==========
  "memory.prompts.memory_update_mode": {
    type: FieldType.SELECT,
    label: "Memory Update Mode",
    help: "Choose to use system default or custom prompt for updating/merging memories",
    section: "memory_prompts",
    options: [
      { value: "system", label: "Use System Default" },
      { value: "custom", label: "Use Custom Prompt" },
    ],
    default: "system",
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.prompts.enable_custom_prompts") === true,
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ["system", "custom"] },
    ],
  },

  "memory.prompts.memory_update_prompt": {
    type: FieldType.TEXTAREA,
    label: "Custom Memory Update Prompt",
    help: 'Custom prompt for updating and merging memories. Click "Reset to System Default" to load the default value.',
    placeholder: "Enter custom memory update prompt (50-4000 characters)...",
    section: "memory_prompts",
    rows: 10,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "memory.prompts.enable_custom_prompts") === true &&
      getConfigValue(config, "memory.prompts.memory_update_mode") === "custom",
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          if (value.length < 50) {
            return {
              valid: false,
              message:
                "Memory update prompt must be at least 50 characters if provided",
            };
          }
          if (value.length > 4000) {
            return {
              valid: false,
              message: "Memory update prompt cannot exceed 4000 characters",
            };
          }
          return { valid: true };
        },
      },
    ],
  },

  // ========== Memory Query Configuration (hooks.user_facts, hooks.memory_context) ==========
  // Fields show only when their corresponding hook is enabled in the Hooks Pipeline
  // Note: memory_context hook now handles both user_events AND agent_memories (unified in single hook)
  "hooks.user_facts.facts_limit": {
    type: FieldType.NUMBER,
    label: "Facts Query Limit",
    help: "Maximum number of permanent user facts to retrieve (1-100)",
    section: "memory_query",
    min: 1,
    max: 100,
    default: 20,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.user_facts") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 100 },
    ],
  },

  "hooks.memory_context.events_limit": {
    type: FieldType.NUMBER,
    label: "Events Query Limit",
    help: "Maximum number of temporal event memories to retrieve (1-50)",
    section: "memory_query",
    min: 1,
    max: 50,
    default: 10,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_context") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 50 },
    ],
  },

  "hooks.memory_context.events_threshold": {
    type: FieldType.NUMBER,
    label: "Events Search Threshold",
    help: "Similarity threshold for event search (0.0-1.0). Lower values return more results.",
    section: "memory_query",
    min: 0.0,
    max: 1.0,
    step: 0.1,
    default: 0.5,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_context") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 1.0 },
    ],
  },

  // Agent memories config - now part of unified memory_context hook
  "hooks.memory_context.agent_memories_limit": {
    type: FieldType.NUMBER,
    label: "Agent Memories Limit",
    help: "Maximum number of agent-specific memories to retrieve (1-20)",
    section: "memory_query",
    min: 1,
    max: 20,
    default: 5,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_context") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 20 },
    ],
  },

  "hooks.memory_context.agent_memories_threshold": {
    type: FieldType.NUMBER,
    label: "Agent Memories Threshold",
    help: "Similarity threshold for agent memory search (0.0-1.0). Lower values return more results.",
    section: "memory_query",
    min: 0.0,
    max: 1.0,
    step: 0.1,
    default: 0.5,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_context") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 1.0 },
    ],
  },

  // Shared query settings - show if ANY memory hook is visible
  "hooks.memory_context.query_max_length": {
    type: FieldType.NUMBER,
    label: "Query Max Length",
    help: "Maximum total length of memory search query in characters (100-1000)",
    section: "memory_query",
    min: 100,
    max: 1000,
    step: 50,
    default: 500,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      (getConfigValue(config, "hooks_visible.user_facts") !== false ||
        getConfigValue(config, "hooks_visible.memory_context") !== false),
    validation: [
      { rule: ValidationRuleType.MIN, value: 100 },
      { rule: ValidationRuleType.MAX, value: 1000 },
    ],
  },

  "hooks.memory_context.user_query_length": {
    type: FieldType.NUMBER,
    label: "User Query Length",
    help: "Max length of current user question in search query (50-500 chars)",
    section: "memory_query",
    min: 50,
    max: 500,
    step: 50,
    default: 250,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      (getConfigValue(config, "hooks_visible.user_facts") !== false ||
        getConfigValue(config, "hooks_visible.memory_context") !== false),
    validation: [
      { rule: ValidationRuleType.MIN, value: 50 },
      { rule: ValidationRuleType.MAX, value: 500 },
    ],
  },

  "hooks.memory_context.assistant_context_length": {
    type: FieldType.NUMBER,
    label: "Assistant Context Length",
    help: "Max length of last assistant response in search query (0-500 chars). Set to 0 to disable assistant context.",
    section: "memory_query",
    min: 0,
    max: 500,
    step: 50,
    default: 200,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      (getConfigValue(config, "hooks_visible.user_facts") !== false ||
        getConfigValue(config, "hooks_visible.memory_context") !== false),
    validation: [
      { rule: ValidationRuleType.MIN, value: 0 },
      { rule: ValidationRuleType.MAX, value: 500 },
    ],
  },

  // ========== Memory Decay (hooks.memory_reinforce) ==========
  "hooks.memory_reinforce.enabled": {
    type: FieldType.CHECKBOX,
    label: "Enable Memory Decay",
    help: "Enable Ebbinghaus forgetting curve for temporal memories. Memories decay over time based on access frequency.",
    section: "memory_ebbinghaus",
    default: false,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_reinforce") !== false,
  },

  "hooks.memory_reinforce.decay_factor": {
    type: FieldType.NUMBER,
    label: "Decay Factor",
    help: "Controls decay speed (1.0=fast forget, 30.0=slow forget). Higher values mean slower memory decay.",
    section: "memory_ebbinghaus",
    min: 1.0,
    max: 30.0,
    step: 1.0,
    default: 7.0,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_reinforce") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1.0 },
      { rule: ValidationRuleType.MAX, value: 30.0 },
    ],
  },

  "hooks.memory_reinforce.min_retention": {
    type: FieldType.NUMBER,
    label: "Min Retention",
    help: "Minimum retention floor (0.0-0.5). Memories never decay below this value. Default 0.1 means memories retain at least 10% relevance.",
    section: "memory_ebbinghaus",
    min: 0.0,
    max: 0.5,
    step: 0.05,
    default: 0.1,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_reinforce") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 0.5 },
    ],
  },

  "hooks.memory_reinforce.initial_strength": {
    type: FieldType.NUMBER,
    label: "Initial Strength",
    help: "Starting memory strength for new memories (0.5-2.0). Higher values make new memories more resistant to decay.",
    section: "memory_ebbinghaus",
    min: 0.5,
    max: 2.0,
    step: 0.1,
    default: 1.0,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_reinforce") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.5 },
      { rule: ValidationRuleType.MAX, value: 2.0 },
    ],
  },

  "hooks.memory_reinforce.reinforcement_boost": {
    type: FieldType.NUMBER,
    label: "Reinforcement Boost",
    help: "Strength increase when memory is accessed (0.1-1.0). Higher values strengthen memories more on each access.",
    section: "memory_ebbinghaus",
    min: 0.1,
    max: 1.0,
    step: 0.1,
    default: 0.3,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_reinforce") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.1 },
      { rule: ValidationRuleType.MAX, value: 1.0 },
    ],
  },

  "hooks.memory_reinforce.max_strength": {
    type: FieldType.NUMBER,
    label: "Max Strength",
    help: "Maximum memory strength cap (1.0-5.0). Limits how strong a memory can become through reinforcement.",
    section: "memory_ebbinghaus",
    min: 1.0,
    max: 5.0,
    step: 0.5,
    default: 3.0,
    showIf: (config) =>
      getConfigValue(config, "memory.enabled") === true &&
      getConfigValue(config, "memory.provider") === "MEM0" &&
      getConfigValue(config, "hooks_visible.memory_reinforce") !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1.0 },
      { rule: ValidationRuleType.MAX, value: 5.0 },
    ],
  },

  // ========== System Prompts ==========
  instructions: {
    type: FieldType.MARKDOWN,
    label: "System Instructions",
    placeholder: "Enter system instructions in Markdown...",
    help: "Main system prompt that defines agent personality and behavior",
    section: "prompts",
    rows: 15,
    default: "",
  },

  additional_context: {
    type: FieldType.MARKDOWN,
    label: "Additional Context",
    placeholder: "Enter additional context in Markdown...",
    help: "Extra context appended to system instructions",
    section: "prompts",
    rows: 8,
    default: null,
  },

  // ========== Quick Response Section ==========
  // Prompt fields auto-generated from backend schema (see generated/fieldConfig.generated.ts)
  // Fields: hooks.classifier.use_system_instructions, hooks.classifier.quick_response_instructions
  // Entire section hidden when hooks.quick_chat.enabled is off (controlled in hooks panel)

  // ========== First Message Hook (hooks.first_message) ==========
  "hooks.first_message.enabled": {
    type: FieldType.CHECKBOX,
    label: "Enable First Message Hook",
    help: "Add special instruction for first message in new sessions",
    section: "first_message",
    default: true,
    showIf: (config) =>
      getConfigValue(config, "hooks_visible.first_message") !== false,
  },

  "hooks.first_message.mode": {
    type: FieldType.SELECT,
    label: "First Message Mode",
    help: "Use system default or custom instruction",
    section: "first_message",
    options: [
      { value: "system", label: "Use System Default" },
      { value: "custom", label: "Use Custom Instruction" },
    ],
    default: "system",
    showIf: (config) =>
      getConfigValue(config, "hooks_visible.first_message") !== false,
  },

  "hooks.first_message.custom_instruction": {
    type: FieldType.TEXTAREA,
    label: "Custom First Message Instruction",
    placeholder: "Enter custom instruction for first message...",
    help: "Custom instruction shown only in first message of new sessions",
    section: "first_message",
    rows: 5,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, "hooks_visible.first_message") !== false &&
      getConfigValue(config, "hooks.first_message.mode") === "custom",
  },

  // ========== Response Classifier (Advanced Settings) ==========
  // Now auto-generated from backend schema (see generated/fieldConfig.generated.ts)

  // Skills configuration is now managed directly in the Skills tab (SkillsTab.tsx)
  // Fields: skills_enabled, semantic_matching_enabled, semantic_similarity_threshold

  // ========== Tool Results Compression (override generated config for showIf) ==========
  "tools.compression.model_id": {
    type: FieldType.MODEL_SELECTOR,
    label: "压缩模型",
    section: "tools_compression",
    help: "留空则使用任务模型 (models.task.model_id)",
    default: null,
    optionsSource: "chatModels",
    allowEmpty: true,
    emptyLabel: "使用任务模型",
    showIf: (config) =>
      getConfigValue(config, "tools.compression.enabled") === true,
  },
};

/**
 * Convert showIfPath string to showIf function
 * This allows backend schema to define simple showIf conditions as strings
 */
function processShowIfPath(config: FieldConfig): FieldConfig {
  const result: FieldConfig = {};

  for (const [key, field] of Object.entries(config)) {
    const processedField = { ...field };

    // Convert showIfPath string to showIf function
    // Supports negation prefix: "!path" means show when path is falsy
    if (
      "showIfPath" in processedField &&
      typeof processedField.showIfPath === "string"
    ) {
      const raw = processedField.showIfPath;
      const negate = raw.startsWith("!");
      const path = negate ? raw.slice(1) : raw;
      processedField.showIf = negate
        ? (cfg: any) => !getConfigValue(cfg, path)
        : (cfg: any) => getConfigValue(cfg, path) === true;
      delete processedField.showIfPath;
    }

    result[key] = processedField;
  }

  return result;
}

/**
 * Final merged field configuration
 * Order: Manual overrides first (defines field ordering), then generated-only fields
 *
 * Manual overrides take precedence for fields that need frontend-specific
 * customization (complex showIf, validation, linkedParameters, etc.)
 * Key ordering follows FIELD_CONFIG to ensure proper display order (e.g., chat models before reranking).
 */
const RAW_MERGED_CONFIG: FieldConfig = {
  ...FIELD_CONFIG,
  ...Object.fromEntries(
    Object.entries(GENERATED_FIELD_CONFIG).filter(
      ([key]) => !(key in FIELD_CONFIG),
    ),
  ),
};

export const MERGED_FIELD_CONFIG: FieldConfig =
  processShowIfPath(RAW_MERGED_CONFIG);

export default MERGED_FIELD_CONFIG;
